/**
 * DepositArea — "has the customer put money down, and is it papered?"
 *
 * THIS AREA IS FIRST BECAUSE THE MONEY IS FIRST. Twenty to thirty per cent of deposits are paid
 * before the contract exists, and the deposit is the one thing in the whole journey the customer
 * has already done — so it opens the dossier and then collapses to a one-line dated receipt for
 * the rest of the week.
 *
 * THE NORMAL MOVE IS "FIND IT", NOT "RECORD IT". Twenty-six deposits are sitting unassigned right
 * now at a median age of fifty-one days, so the pool is the first control and every row in it
 * carries its age in words — `51 days in the pool` is what makes an old payment recognisable when
 * the amount alone is not. Recording a new one sits beside it, equally visible, because the other
 * quarter of the time the money arrived after the contract did.
 *
 * TWO CONTROLS, NOT A POPOVER INSIDE A POPOVER. The picker's create panel would have to hold a
 * transaction picker — a Radix popover portalled out of the focus scope of the popover that owns
 * it — and a create form that fights the surface it opened from is worse than the sheet it
 * replaces. So the pool is the picker and recording a new payment is a group below it, both drawn
 * at rest, neither hidden behind the other.
 *
 * A LINK IS AN ACT; A RECORD IS A COMMIT. Pointing this contract at a deposit that already exists
 * is one column on one row and the row that made it can undo it — behind an AlertDialog, because
 * unlinking money is not a typo-level mistake. Recording a NEW payment writes a row that did not
 * exist, so it goes through the block's own commit bar like every other record on the canvas.
 *
 * THERE IS NO DEPOSITOR-IDENTITY SECTION, AND THAT IS A CORRECTION, NOT AN OMISSION.
 * `agreement_create` sets a typed signer only when the deposit has NO account-holding driver
 * (`20260801060000:1439`); on a deposit attached to a contract whose driver has a profile, typed
 * parameters are DISCARDED and `agreement_merge_data` resolves `driver.full_name` / `driver.phone`
 * through COALESCE(profile, external) — profile first. Every deposit on this page is attached, so
 * a form asking who signed would be a form whose answers the server throws away. What replaces it
 * is one derived sentence naming the person the document WILL print, and a link to the only place
 * that can change it.
 *
 * NEITHER BLOCK IS OWED UNTIL A PAYMENT IS, AND BOTH ARE THE MOMENT ONE IS. A contract may be
 * activated with no deposit at all [D-B41] — `sign_contract` stopped refusing over the absence, and
 * `contract.deposit_amount` (a required merge field of the hire purchase template) resolves '0.00'
 * on a contract that carries no `contract_deposits` row, so the Schedule renders and sends. Record
 * one payment and both halves land at once: the money is a render-blocker whose LINKED ZERO still
 * resolves NULL and still stops the send, and every deposit taken has to carry a sealed agreement
 * before the contract can start [D-B15].
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { formatBusinessDate, formatCurrency } from '@/lib/format';
import { useDepositCacheInvalidator } from '@/hooks/useContractDeposits';

import DepositAgreementBlock from '@/components/contracts/deposits/DepositAgreementBlock';
import { depositAgreementBeat } from '@/components/contracts/deposits/depositAgreement';
import { fetchDepositAgreement } from '@/lib/agreements/api';

import { createContractDeposit } from '../data/deposits';
import { depositTotals, type ContractDeposit } from '../data/depositShapes';
import {
    depositAgeText,
    depositDate,
    depositDateText,
    depositLabel,
    depositMeta,
    depositRemarksText,
    depositSearchText,
    type PickableDeposit,
} from '../data/depositPick';
import {
    fetchUnassignedDeposits,
    saveDepositLinks,
    unassignedDepositsKey,
} from '../data/financials';
import { DEPOSIT_RECORD_REFUSALS, fieldErrorsFrom } from '../data/refusalFields';
import InlineFieldGroup from '../inline/InlineFieldGroup';
import InlinePickerPopover, { type PickerRow } from '../inline/InlinePickerPopover';
import MoneyRow from '../inline/MoneyRow';
import TransactionPicker from '../inline/TransactionPicker';
import QuietField from '../inline/QuietField';
import type { FieldValues } from '../inline/inlineEditReducer';
import { useInlineGroup } from '../inline/useInlineGroup';
import type { SetupArea } from '../steps';
import { Block, Blocks, Hint, LinkLike, Rec, Rows } from './blocks';

interface Props {
    contractId: number;
    deposits: ContractDeposit[] | undefined;
    loading: boolean;
    /** The name and phone the deposit agreement will print, from the driver's profile. */
    driverName: string | null;
    driverPhone: string | null;
    onGoToArea: (area: SetupArea) => void;
    onSuccess: () => void;
}

/**
 * The four beats of a deposit agreement, as a strip.
 *
 * A DEPOSIT AGREEMENT IS A CEREMONY, NOT A FIELD, and an admin who has never sent one has no way
 * to know it takes four moves and that two of them are somebody else's. The strip says so before
 * the first button is pressed, and marks where this document has got to.
 */
const BEATS = ['Create', 'Send link', 'Depositor signs', 'Countersign & seal'] as const;

/**
 * The strip, over the same document the block underneath reads.
 *
 * ONE READ, TWO SUBSCRIBERS. The legacy arc the deposit row carries writes `signed_at` at the
 * countersignature and `agreement_url` at the seal, so it cannot tell a document that has just
 * been created from one the depositor has already signed — the strip said `Send link` while the
 * block said `Waiting on your countersignature`. So it takes the block's own query, by its key,
 * and the two are the same reading of the same document; the arc stays as the fallback the first
 * paint has and the paper path never leaves.
 */
function LifecycleStrip({ deposit }: { deposit: ContractDeposit }) {
    const { data } = useQuery({
        queryKey: ['deposit-agreement', deposit.id],
        queryFn: () => fetchDepositAgreement(deposit.id),
        enabled: !!deposit.id,
    });

    const at = depositAgreementBeat({
        arc: data?.arc ?? deposit.agreement,
        document: data?.document ?? null,
    });

    return (
        <div className="sd-beats" aria-label="Deposit agreement lifecycle">
            {BEATS.map((beat, index) => (
                <span
                    key={beat}
                    className={
                        index < at ? 'sd-beat sd-beat-done' : index === at ? 'sd-beat sd-beat-now' : 'sd-beat'
                    }
                >
                    <span className="sd-beat-n">{index + 1}</span>
                    {beat}
                </span>
            ))}
        </div>
    );
}

export default function DepositArea({
    contractId,
    deposits,
    loading,
    driverName,
    driverPhone,
    onGoToArea,
    onSuccess,
}: Props) {
    const queryClient = useQueryClient();
    const invalidateDepositCaches = useDepositCacheInvalidator();
    const [unlinking, setUnlinking] = useState<ContractDeposit | null>(null);
    const [recording, setRecording] = useState(false);
    /**
     * The server's own sentence for whichever act was refused, held for the surface that asked.
     *
     * NOT A TOAST. A refusal about this contract's deposit outlives the toast that carried it and
     * lands nowhere an admin can act on — while every other refusal on this canvas is verbatim, on
     * the control that raised it, and stays there. The two acts share one slot because only one of
     * them can be in flight: the unlink is read inside its own dialog while that is open, and the
     * link is read under the picker.
     */
    const [refusal, setRefusal] = useState<string | null>(null);

    const rows = deposits ?? [];
    const totals = depositTotals(rows);

    const { data: pool, isLoading: poolLoading } = useQuery({
        queryKey: unassignedDepositsKey,
        queryFn: fetchUnassignedDeposits,
    });

    /** The shared matrix: this contract's read, the analytics, the pool, the board's subjects. */
    const settle = () => {
        invalidateDepositCaches({ contractId });
        queryClient.invalidateQueries({ queryKey: ['contract-setup'] });
        onSuccess();
    };

    const link = useMutation({
        mutationFn: (depositId: number) =>
            saveDepositLinks({ contractId, depositsToLink: [depositId], depositsToUnlink: [] }),
        onSuccess: () => {
            toast.success('Deposit linked to this contract');
            setRefusal(null);
            settle();
        },
        onError: (error: Error) => setRefusal(error.message || 'The link was refused.'),
    });

    const unlink = useMutation({
        mutationFn: (depositId: number) =>
            saveDepositLinks({ contractId, depositsToLink: [], depositsToUnlink: [depositId] }),
        onSuccess: () => {
            toast.success('Deposit returned to the unassigned pool');
            setUnlinking(null);
            setRefusal(null);
            settle();
        },
        // THE DIALOG STAYS OPEN. Closing it on a refusal takes the sentence away from the decision
        // it was about and leaves the payment looking unlinked-and-refused at the same time.
        onError: (error: Error) => setRefusal(error.message || 'The unlink was refused.'),
    });

    /**
     * The record-new group.
     *
     * ITS SERVER VALUES ARE ALWAYS EMPTY, and the memo is keyed on the deposit LIST so that a
     * successful save — which changes that list — is what clears the fields. Keyed on nothing, the
     * sync effect would never fire again and the block would sit there still holding the payment
     * it had already recorded.
     */
    const recordServer = useMemo<FieldValues>(
        () => ({ transactionId: '', remarks: '' }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [deposits],
    );

    const record = useInlineGroup({
        server: recordServer,
        fieldErrorsFor: fieldErrorsFrom(DEPOSIT_RECORD_REFUSALS),
        save: (draft) =>
            createContractDeposit({
                contractId,
                transactionId: draft.transactionId === '' ? null : Number(draft.transactionId),
                remarks: draft.remarks,
            }),
        onSaved: () => {
            setRecording(false);
            settle();
        },
    });

    const poolRows: PickerRow<PickableDeposit>[] = (pool ?? []).map((deposit) => ({
        id: String(deposit.id),
        search: depositSearchText(deposit),
        item: deposit,
    }));

    return (
        <Blocks>
            <Block id="b-deposit-payment" title="Payments">
                {loading ? (
                    <div className="flex justify-center py-6">
                        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    </div>
                ) : rows.length === 0 ? (
                    <>
                        <p className="sd-lede sd-dim">
                            No deposit is linked to this contract yet. Roughly a quarter of deposits are
                            paid before the car is chosen, so the money is usually already in the bank —
                            look in the unassigned pool before recording a new one.
                        </p>
                        {/* WHAT THE SCHEDULE DOES WITHOUT ONE, STATED RATHER THAN THREATENED [D-B41].
                            This hint used to read "and will not render without it", which stopped
                            being true when the merge query gained its zero-row arm: the document
                            renders, the contract activates, and the only thing a contract with no
                            deposit loses is a figure on one row. */}
                        <Hint>
                            The hire purchase agreement prints the deposit as the Deposit credited row
                            of its money particulars. With nothing linked it credits GHS 0.00, and the
                            contract can still be activated.
                        </Hint>
                    </>
                ) : (
                    <>
                        {rows.map((deposit, index) => (
                            <Rows key={deposit.id} className={index > 0 ? 'mt-6' : undefined}>
                                <Rec
                                    label={rows.length > 1 ? `Payment ${index + 1}` : 'Amount'}
                                    value={
                                        <span className="sd-num">
                                            {formatCurrency(deposit.amount, deposit.currency)}
                                        </span>
                                    }
                                    sub={deposit.description ?? undefined}
                                />
                                <Rec
                                    label="Paid on"
                                    value={formatBusinessDate(deposit.transaction_at)}
                                    sub={
                                        <LinkLike
                                            onClick={() => {
                                                setRefusal(null);
                                                setUnlinking(deposit);
                                            }}
                                        >
                                            Unlink this payment
                                        </LinkLike>
                                    }
                                />
                                {deposit.remarks && <Rec label="Remarks" value={deposit.remarks} />}
                                {/* NO REFUND UI — the table has never held a single row. A refund that
                                    somehow exists is annotated and linked out, never edited here. */}
                                {deposit.refunded > 0 && (
                                    <Rec
                                        label="Refunded"
                                        value={
                                            <span className="sd-num">
                                                −{formatCurrency(deposit.refunded, deposit.currency)}
                                            </span>
                                        }
                                        sub={`${deposit.refund_count} refund${deposit.refund_count === 1 ? '' : 's'} · managed on the Deposits page`}
                                    />
                                )}
                            </Rows>
                        ))}

                        {/* A TOTAL ONLY WHERE THERE IS SOMETHING TO TOTAL. 92.7% of contracts have
                            exactly one deposit, and a sum strip over a single payment is arithmetic
                            theatre. */}
                        {rows.length > 1 && (
                            <div className="sd-sum">
                                <span className="sd-sum-v sd-num">{formatCurrency(totals.total)}</span>
                                <span className="sd-sum-k">
                                    total of {totals.count} payments — this is the figure that prints on
                                    the hire purchase agreement as the Deposit credited row
                                </span>
                            </div>
                        )}
                    </>
                )}

                <Rows className="mt-4">
                    {/* THE POOL, AGE-ANNOTATED. Nothing is selected here and nothing ever will be:
                        the row's job is to attach a payment, and once attached it is drawn above as
                        a payment rather than as a picker's value. */}
                    <InlinePickerPopover<PickableDeposit>
                        id="f-depositLink"
                        label="Link a payment"
                        rows={poolRows}
                        selectedId={null}
                        selectedLabel=""
                        emptyLabel={
                            link.isPending
                                ? 'Linking…'
                                : poolRows.length === 0
                                  ? 'Nothing unassigned'
                                  : `${poolRows.length} unassigned ${poolRows.length === 1 ? 'payment' : 'payments'}`
                        }
                        searchPlaceholder="Search by amount, date or remarks"
                        noun="unassigned payment"
                        // THE SAME ANATOMY THE TRANSACTION PICKERS DRAW, plus the age. Drawn as a
                        // label and a meta, this row shipped `GH₵5,000.…`: the meta — a date, an
                        // age and a free-text remark carrying somebody's name and phone number —
                        // sized itself first and took the amount's own column. The amount is what
                        // is being recognised, so it now leads in a column nothing can take from,
                        // the date and the age hold steady cells behind it, and the remarks are
                        // the one cell that gives way (with the full text on its own `title`).
                        renderLabel={(deposit) => (
                            <MoneyRow
                                amount={depositLabel(deposit)}
                                dated={depositDateText(deposit) || 'no date'}
                                age={depositAgeText(depositDate(deposit))}
                                meta={depositRemarksText(deposit) || 'no remarks'}
                            />
                        )}
                        rowTitle={(deposit) => `${depositLabel(deposit)} · ${depositMeta(deposit)}`}
                        // 600, the transaction row's width, and for the transaction row's reason:
                        // the row ends in remarks — or, where there are none, in the bank's own
                        // description — which is the one string here with no ceiling. The three
                        // steady cells in front of it cost 296px + gaps, so 600 leaves the
                        // remarks a little over 200px and the amount is never among what gives.
                        width={600}
                        onSelect={(row) => {
                            setRefusal(null);
                            link.mutate(Number(row.id));
                        }}
                        loading={poolLoading}
                        disabled={link.isPending}
                        // The unlink's refusal is read inside its own dialog; this row carries
                        // only the one it raised itself.
                        error={unlinking ? null : refusal}
                        hint="money already in the bank · attaches as soon as you pick it"
                    />
                </Rows>

                {recording ? (
                    <div className="mt-4">
                        <InlineFieldGroup
                            group={record}
                            footnote="Only for money that is not in the pool. The transaction row is what the deposit is; the remarks are for whoever reads it next."
                        >
                            <Rows>
                                <TransactionPicker
                                    id="f-depositTxn"
                                    label="Bank transaction"
                                    type="CREDIT"
                                    value={record.draft.transactionId}
                                    onChange={(value) => record.set('transactionId', value)}
                                    dirty={record.field('transactionId').dirty}
                                    error={record.field('transactionId').error}
                                    disabled={record.saving}
                                    emptyLabel="Not chosen — required"
                                    hint="the credit that arrived"
                                />
                                <QuietField
                                    {...record.field('remarks')}
                                    id="f-depositRemarks"
                                    label="Remarks"
                                    placeholder="Who paid, and how"
                                />
                            </Rows>
                            <div className="sd-b-actions">
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                        record.discard();
                                        setRecording(false);
                                    }}
                                >
                                    Cancel this payment
                                </Button>
                            </div>
                        </InlineFieldGroup>
                    </div>
                ) : (
                    <div className="sd-b-actions">
                        <Button variant="outline" size="sm" onClick={() => setRecording(true)}>
                            Record a new payment
                        </Button>
                    </div>
                )}
            </Block>

            {/*
              * [D-B15] — THE WORD "OPTIONAL" NEVER APPEARS ON THIS BLOCK, IN ANY STATE.
              *
              * The deposit agreement papers money the driver has already handed over, and it was
              * optional on every contract while `contract_deposit_agreements` held zero rows
              * against 249 deposits. It is required now, per deposit — so the eyebrow reads
              * `Required` the moment a payment is linked, and with nothing linked it reads NOTHING
              * at all. The old dormant `Optional` aside was the owner's complaint verbatim: the
              * word reads as permission to skip, and a document the server refuses to activate
              * without cannot wear it in any state, including the window before its subject exists.
              *
              * THE SILENT EYEBROW IS THE HONEST ONE UNDER [D-B41] AND NOT MERELY THE TACTFUL ONE.
              * A contract with no deposit is activatable, so there is genuinely no requirement to
              * announce yet — which is the same fact the rail draws as a grey dot on both deposit
              * rows. The lede below names the rule that lands the moment a payment arrives.
              */}
            <Block
                id="b-deposit-agreement"
                title="Deposit agreement"
                aside={rows.length > 0 ? 'Required' : undefined}
            >
                {rows.length === 0 ? (
                    <p className="sd-lede sd-dim">
                        Nothing to paper yet. The agreement is generated against a payment, so it becomes
                        available once one is linked — and it is required from that moment: every payment
                        on the contract has to carry a signed agreement before it can be activated.
                    </p>
                ) : (
                    <>
                        <p className="sd-lede sd-dim">
                            {rows.length === 1
                                ? 'Required to activate. The payment above has to carry a signed agreement, executed and filed, before this contract can start.'
                                : `Required to activate. Each of the ${rows.length} payments above has to carry its own signed agreement, executed and filed, before this contract can start.`}
                        </p>

                        {/* THE DERIVED LINE, NOT A FORM. `agreement_create` binds the depositor's
                            seat to the driver's profile whenever the deposit's contract has one and
                            DISCARDS any typed name and phone, so the only honest thing to show is
                            who the document will print and where that comes from. */}
                        <Hint className="mb-3.5">
                            Signs as{' '}
                            <b>{driverName || 'the driver'}</b>
                            {driverPhone ? ` · ${driverPhone}` : ''}, from the driver&rsquo;s profile.{' '}
                            <LinkLike onClick={() => onGoToArea('driver')}>Edit in Driver</LinkLike>
                        </Hint>

                        {rows.map((deposit, index) => (
                            <div key={deposit.id} className={index > 0 ? 'mt-8' : undefined}>
                                {rows.length > 1 && (
                                    <p className="sd-hint mb-2">
                                        For the payment of{' '}
                                        {formatCurrency(deposit.amount, deposit.currency)} on{' '}
                                        {formatBusinessDate(deposit.transaction_at)}
                                    </p>
                                )}
                                <LifecycleStrip deposit={deposit} />
                                <DepositAgreementBlock
                                    deposit={deposit}
                                    // The block is mounted INSIDE the page its deep links point at, so a
                                    // hard navigate would be self-navigation and would throw away every
                                    // open draft on the canvas.
                                    onDeepLink={(area) => onGoToArea(area as SetupArea)}
                                />
                            </div>
                        ))}
                    </>
                )}
            </Block>

            <AlertDialog
                open={unlinking !== null}
                onOpenChange={(open) => {
                    if (open) return;
                    setUnlinking(null);
                    setRefusal(null);
                }}
            >
                <AlertDialogContent className="sd-tokens">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Return this payment to the pool?</AlertDialogTitle>
                        <AlertDialogDescription>
                            {unlinking
                                ? `${formatCurrency(unlinking.amount, unlinking.currency)} paid ${formatBusinessDate(unlinking.transaction_at)} stops counting towards this contract's deposit. The Deposit credited row of the hire purchase agreement prints that total, so an unsigned agreement will print the new one — and the payment itself is not deleted, it goes back to the unassigned pool.`
                                : ''}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    {/* Verbatim, where the decision was taken. */}
                    {refusal && <p className="sd-f-err">{refusal}</p>}
                    <AlertDialogFooter>
                        <AlertDialogCancel>Keep it linked</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => unlinking && unlink.mutate(unlinking.id)}
                            disabled={unlink.isPending}
                        >
                            {unlink.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Unlink it
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Blocks>
    );
}
